import React, { useState, useEffect, useMemo } from 'react';
import { ArrowLeft, Plus, Trash2, Save, AlertTriangle, Package, PackageSearch } from 'lucide-react';
import SelecaoPecaCentral, { ENDPOINT_SEM_PRECO } from '../../manutencao/certificacao/SelecaoPecaCentral';
import FotoUpload from '../../manutencao/certificacao/FotoUpload';
import useEhCompacto from '../../../utils/useEhCompacto';

// =====================================================================
// Logística › Retirada de Equipamento — o FORMULÁRIO.
//
// ⚠️⚠️ UM FORMULÁRIO, DUAS FORMAS, E QUEM DECIDE É A **EQUIPE** (pedido da
//    Julia, 10/09: *"quando seleciona a equipe de Manutenção o campo de
//    equipamento some e fica igual à Solicitação de Reparo"*):
//
//      T.I        → escolhe UM EQUIPAMENTO no parque de T.I. Sem peças.
//                   Ao gravar, o portal emite o COMPROVANTE em PDF que
//                   acompanha o aparelho.
//      Manutenção → lista as PEÇAS necessárias (Central de Pedidos ou fora
//                   dela). Sem campo de equipamento — é o formulário de reparo
//                   de sempre.
//
// ⚠️⚠️ ESTE ARQUIVO É A FUSÃO DE DOIS. Até 09/09 existiam o formulário da
//    Solicitação de Reparo (módulo Manutenção, peças) e o da Retirada (aba do
//    Chamados, equipamento). A Solicitação de Reparo **deixou de existir** no
//    módulo Manutenção — ela é a metade "Manutenção" desta tela.
//
// ⚠️ UMA TELA E NÃO DUAS, e a decisão inverte a de 09/09 (quando foram
//    deliberadamente separadas). O que mudou: naquele dia elas viviam em
//    módulos diferentes e cada uma tinha o seu caminho de entrada; hoje as duas
//    são o MESMO item de menu, e duas telas atrás de um seletor obrigariam a
//    pessoa a descobrir qual delas abre — quando a pergunta que ela responde
//    ("isto é da T.I ou da Manutenção?") já é o primeiro campo.
//
// ⚠️ A VALIDAÇÃO QUE VALE É A DO SERVIDOR (`retirada-validacao.js`, pura e
// testada). Aqui só se impede o envio óbvio-vazio, para a pessoa não descobrir
// pelo 400 que esqueceu um campo. Duplicar a regra inteira criaria um espelho
// que divergiria no primeiro ajuste — e o front nem é a autoridade sobre o
// recorte de loja.
//
// ⚠️ O CATÁLOGO VEM SEM PREÇO (`ENDPOINT_SEM_PRECO`). Este formulário é
// preenchido também pelo Gerente, e a leitura com preço não o inclui de
// propósito — ver a nota do GET /catalogo/selecao em server-compras.js.
// =====================================================================

const API = '/api/logistica/retirada';
const COR = '#0e7490';
const COR_ESCURA = '#155e75';
const COR_CLARA = '#ecfeff';
const COR_BORDA = '#a5f3fc';

const auth = () => ({ Authorization: `Bearer ${localStorage.getItem('authToken')}` });

// Uma linha de peça vazia. A origem nasce SEM escolha: "da Central" é o caso
// mais comum e seria o padrão óbvio, mas o que já vem marcado ninguém lê — e
// aqui a origem muda o que se preenche.
const PECA_VAZIA = () => ({
  uid: Math.random().toString(36).slice(2),
  // ⚠️ `fornecedor` = de quem se compra a peça; `fornecedorReparo` = quem conserta.
  //    O segundo é POR PEÇA (cada peça é uma solicitação de reparo).
  origem: '', idCatalogo: null, codigo: '', descricao: '', fornecedor: '', fornecedorReparo: '', quantidade: '1',
});

const rotulo = { display: 'block', fontSize: 13, fontWeight: 700, color: '#334155', marginBottom: 6 };
const campo = {
  width: '100%', padding: '9px 11px', border: '1px solid #cbd5e1', borderRadius: 8,
  fontSize: 14, color: '#0f172a', boxSizing: 'border-box', fontFamily: 'inherit',
};

// As duas equipes e o que cada uma muda na tela.
//
// ⚠️ MAPA e não `equipe === 'TI' ? … : …` espalhado: as diferenças são várias
//    (rótulo do campo de prosa, o que aparece, o texto de ajuda), e um ternário
//    por diferença é como elas passam a discordar entre si — uma tela dizendo
//    "motivo da retirada" enquanto a outra metade já mostra peças.
const EQUIPES = {
  TI: {
    rotulo: 'T.I',
    usaPecas: false,
    rotuloProsa: 'Motivo da retirada',
    dicaProsa: 'Por que o equipamento precisa sair? Vai para conserto, substituição, descarte?',
    resumo: 'Identifique o equipamento e explique o motivo da retirada. Ao gravar, o portal emite o comprovante em PDF que acompanha o aparelho.',
  },
  Manutencao: {
    rotulo: 'Manutenção',
    usaPecas: true,
    rotuloProsa: 'Descrição do problema',
    dicaProsa: 'O que está acontecendo? Onde, desde quando, o que já foi tentado?',
    resumo: 'Descreva o problema e liste as peças necessárias para resolvê-lo.',
  },
};

const RetiradaFormulario = ({ onVoltar, onGravado }) => {
  const compacto = useEhCompacto();

  const [equipe, setEquipe] = useState('');
  const [lojas, setLojas] = useState([]);
  const [loja, setLoja] = useState('');
  const [motivo, setMotivo] = useState('');
  const [fotoUrl, setFotoUrl] = useState('');

  // Fornecedor do reparo do T.I — quem VAI REALIZAR o reparo do equipamento.
  // Opcional. ⚠️ Só no T.I: lá o item é único. Na Manutenção o fornecedor do
  // reparo é POR PEÇA (mora no estado de cada peça).
  // ⚠️ MAIÚSCULO na digitação (pedido da Julia): o servidor grava em caixa alta e
  //    a tela reflete isso na hora, para o que se vê ser o que grava.
  const [fornecedorReparo, setFornecedorReparo] = useState('');
  // As sugestões do autocomplete: os fornecedores de reparo já usados antes. No
  // T.I, do MESMO equipamento; na Manutenção, da MESMA loja (ver o efeito abaixo).
  // Compartilhadas pelo campo do T.I e pelos campos por peça da Manutenção — as
  // duas equipes nunca aparecem juntas na tela, então um <datalist> só basta.
  const [sugestoesFornecedor, setSugestoesFornecedor] = useState([]);

  // O caminho de T.I: o equipamento, escolhido no parque.
  // ⚠️ Só o `ativoCodigo` vai para o servidor: código e nome gravados são
  //    resolvidos LÁ, a partir dele. O que esta tela mostra é para a pessoa
  //    conferir — mesma regra da peça da Central.
  const [equipamentos, setEquipamentos] = useState([]);
  const [ativoCodigo, setAtivoCodigo] = useState('');
  // ⚠️⚠️ A SÉRIE É DIGITADA, não vem do parque — e isso foi medido: a
  //    `dtbChamadoAtivo` tem 1.824 equipamentos ativos e **zero** com número de
  //    série cadastrado. Quem consegue lê-la é quem está com o aparelho na mão.
  const [numeroSerie, setNumeroSerie] = useState('');
  // ⚠️ Foto do número de série — OBRIGATÓRIA no T.I. A série é digitada olhando a
  //    etiqueta e erro de digitação passa batido; a foto é a prova para conferir
  //    a leitura. Sobe no MESMO fluxo/pasta da foto geral (URL depois no POST).
  const [fotoSerieUrl, setFotoSerieUrl] = useState('');

  // O caminho da Manutenção: as peças.
  const [pecas, setPecas] = useState([PECA_VAZIA()]);

  const [enviando, setEnviando] = useState(false);
  const [erro, setErro] = useState('');

  // Agrupa as fotos desta solicitação numa pasta só no SharePoint. Nasce aqui
  // porque o upload acontece ANTES de a solicitação existir.
  const chaveArquivos = useMemo(() => `R${Date.now().toString(36)}`, []);

  const cfg = EQUIPES[equipe] || null;

  // As unidades que a equipe escolhida oferece.
  //
  // ⚠️⚠️ A LISTA DEPENDE DA EQUIPE, e não é detalhe: para a Manutenção valem só
  //    as unidades do programa; para o T.I o **escritório também vale** — é onde
  //    o time de TI fica, e o parque de lá tem equipamento. Uma lista só
  //    esconderia do TI justamente a unidade dele.
  // ⚠️ Trocar a equipe LIMPA a unidade escolhida: a sigla pode nem existir na
  //    lista nova, e um `<select>` mostrando um valor fora das opções fica
  //    visualmente em branco — a pessoa acharia que não escolheu.
  useEffect(() => {
    setLoja('');
    setLojas([]);
    if (!equipe) return;
    (async () => {
      try {
        const r = await fetch(`${API}/lojas?equipe=${encodeURIComponent(equipe)}`, { headers: auth() });
        if (!r.ok) throw new Error('falha');
        const lista = await r.json();
        setLojas(lista);
        // Uma unidade só (o caso do Gerente): já vem escolhida. Não há o que
        // escolher, e obrigar o clique é atrito puro.
        if (lista.length === 1) setLoja(lista[0]);
      } catch {
        setErro('Não foi possível carregar as unidades. Recarregue a página.');
      }
    })();
  }, [equipe]);

  // O parque de T.I da unidade escolhida.
  //
  // ⚠️⚠️ RECARREGA A CADA TROCA DE UNIDADE (ou de equipe) E LIMPA a escolha
  //    anterior: sem a limpeza, trocar a loja depois de escolher o equipamento
  //    mandaria ao servidor um código da unidade ANTERIOR — recusado com "está
  //    cadastrado em outra unidade", mensagem que não explica o que a pessoa fez.
  // ⚠️ Só busca no T.I: na Manutenção não existe campo de equipamento, e pedir
  //    o parque à toa custaria uma consulta a cada troca de loja.
  useEffect(() => {
    setAtivoCodigo('');
    // ⚠️ A série limpa JUNTO com o equipamento: ela é do aparelho, e mantê-la ao
    //    trocar de unidade gravaria a série de um monitor no registro de outro —
    //    erro que nenhuma validação pega, porque o campo é texto livre.
    setNumeroSerie('');
    setEquipamentos([]);
    if (!loja || equipe !== 'TI') return;
    (async () => {
      try {
        const r = await fetch(`${API}/equipamentos?loja=${encodeURIComponent(loja)}`, { headers: auth() });
        if (!r.ok) throw new Error('falha');
        setEquipamentos(await r.json());
      } catch {
        setEquipamentos([]);
        setErro('Não foi possível carregar o parque desta unidade. Recarregue a página.');
      }
    })();
  }, [loja, equipe]);

  // As sugestões de fornecedor do reparo — os já usados antes.
  //
  // ⚠️⚠️ A CHAVE DEPENDE DA EQUIPE, igual ao servidor: no T.I as sugestões são do
  //    MESMO equipamento (`ativoCodigo`) — é o objetivo da demanda; na Manutenção,
  //    da MESMA loja (não há equipamento). Por isso o efeito depende dos três, e
  //    recarrega quando o gatilho da equipe muda.
  // ⚠️ Não trava nada: só alimenta o <datalist>. Falha ou lista vazia = a pessoa
  //    digita normalmente (é o caso do primeiro reparo daquele equipamento/loja).
  useEffect(() => {
    setSugestoesFornecedor([]);
    if (!equipe || !loja) return;
    if (equipe === 'TI' && !ativoCodigo) return;
    const qs = equipe === 'TI'
      ? `equipe=TI&ativoCodigo=${encodeURIComponent(ativoCodigo)}`
      : `equipe=Manutencao&loja=${encodeURIComponent(loja)}`;
    (async () => {
      try {
        const r = await fetch(`${API}/fornecedores-reparo?${qs}`, { headers: auth() });
        if (!r.ok) return;
        const lista = await r.json();
        if (Array.isArray(lista)) setSugestoesFornecedor(lista);
      } catch {
        // Sugestão é conveniência; silêncio aqui não prejudica o preenchimento.
      }
    })();
  }, [equipe, loja, ativoCodigo]);

  const escolhido = equipamentos.find((a) => a.CODIGO === ativoCodigo) || null;

  const mexerPeca = (uid, patch) =>
    setPecas((atual) => atual.map((p) => (p.uid === uid ? { ...p, ...patch } : p)));

  const removerPeca = (uid) =>
    setPecas((atual) => (atual.length === 1 ? [PECA_VAZIA()] : atual.filter((p) => p.uid !== uid)));

  // Trocar a origem LIMPA a peça: os campos de uma não servem à outra, e deixar
  // o resto preenchido manda para o servidor um híbrido (peça de catálogo com
  // descrição digitada) que ele recusa — sem a pessoa entender o motivo.
  const trocarOrigem = (uid, origem) =>
    mexerPeca(uid, { origem, idCatalogo: null, codigo: '', descricao: '', fornecedor: '' });

  const enviar = async () => {
    setErro('');
    // ⚠️ A ordem das checagens segue a ordem dos campos NA TELA: apontar o erro
    //    de um campo que está embaixo antes do que está em cima faz a pessoa
    //    rolar para achar o que já tinha preenchido.
    if (!equipe) return setErro('Escolha a equipe: T.I ou Manutenção.');
    if (!loja) return setErro('Escolha a unidade.');
    if (!cfg.usaPecas && !ativoCodigo) return setErro('Escolha o equipamento que vai ser retirado.');
    if (!cfg.usaPecas && !numeroSerie.trim()) return setErro('Informe o número de série do equipamento.');
    if (!cfg.usaPecas && !fotoSerieUrl) return setErro('Anexe a foto do número de série (ela valida a digitação).');
    if (motivo.trim().length < 5) {
      return setErro(cfg.usaPecas ? 'Explique o problema.' : 'Explique o motivo da retirada.');
    }
    if (cfg.usaPecas && !pecas.some((p) => p.origem)) {
      return setErro('Inclua ao menos uma peça ou produto.');
    }

    setEnviando(true);
    try {
      const r = await fetch(API, {
        method: 'POST',
        headers: { ...auth(), 'Content-Type': 'application/json' },
        body: JSON.stringify({
          // ⚠️ É esta linha que decide tudo no servidor: a forma da validação, o
          //    que é gravado como item da esteira e se o e-mail sai.
          equipe,
          loja,
          motivo,
          // ⚠️ Já vai em maiúsculo (o input força), mas o servidor normaliza de
          //    novo — quem grava não confia no que a tela mandou. Vazio → null.
          fornecedorReparo: fornecedorReparo.trim() || null,
          fotoUrl: fotoUrl || null,
          chaveArquivos,
          // ⚠️ Manda só o caminho DA EQUIPE ESCOLHIDA. O outro vai vazio de
          //    propósito — o servidor ignora o que não pertence à equipe, mas
          //    mandar os dois faria a tela parecer ter gravado as duas coisas.
          ativoCodigo: cfg.usaPecas ? null : ativoCodigo,
          numeroSerie: cfg.usaPecas ? null : numeroSerie.trim(),
          fotoSerieUrl: cfg.usaPecas ? null : (fotoSerieUrl || null),
          // ⚠️ Peça da Central manda só o ID: código, descrição e fornecedor são
          //    resolvidos no servidor a partir do catálogo. O que esta tela
          //    mostra é para a PESSOA conferir, não é o que fica gravado.
          // ⚠️ `fornecedorReparo` (quem conserta) vai SEMPRE — nas duas origens,
          //    inclusive na Central: ao contrário de código/descrição/fornecedor,
          //    ele não sai do catálogo, é escolha da pessoa por peça.
          pecas: cfg.usaPecas
            ? pecas.filter((p) => p.origem).map((p) => (p.origem === 'Central'
              ? {
                origem: 'Central', idCatalogo: p.idCatalogo, quantidade: p.quantidade,
                fornecedorReparo: p.fornecedorReparo || null,
              }
              : {
                origem: 'Fora', descricao: p.descricao, codigo: p.codigo,
                fornecedor: p.fornecedor, fornecedorReparo: p.fornecedorReparo || null,
                quantidade: p.quantidade,
              }))
            : [],
        }),
      });
      const corpo = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(corpo.message || 'Não foi possível registrar a solicitação.');
      onGravado?.(corpo.id);
    } catch (e) {
      setErro(e.message);
    } finally {
      setEnviando(false);
    }
  };

  const botaoOrigem = (p, valor, Icone, texto) => {
    const ativo = p.origem === valor;
    return (
      <button
        type="button"
        onClick={() => trocarOrigem(p.uid, valor)}
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 6, cursor: 'pointer',
          padding: '7px 12px', borderRadius: 8, fontSize: 12.5, fontWeight: 700,
          border: `${ativo ? 2 : 1}px solid ${ativo ? COR : '#cbd5e1'}`,
          background: ativo ? COR_CLARA : '#fff', color: ativo ? COR_ESCURA : '#475569',
        }}
      >
        <Icone size={14} /> {texto}
      </button>
    );
  };

  return (
    <div>
      {/* ⚠️ UM ÚNICO <datalist> para todos os campos de fornecedor do reparo (o do
          T.I e os por peça da Manutenção). Vários com o mesmo id é HTML inválido —
          o navegador usaria só o primeiro. As sugestões são as mesmas na tela
          inteira (mesmo equipamento no T.I, mesma loja na Manutenção). */}
      <datalist id="lista-fornecedor-reparo">
        {sugestoesFornecedor.map((f) => <option key={f} value={f} />)}
      </datalist>

      <button
        onClick={onVoltar}
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 6, border: 'none',
          background: 'transparent', color: '#64748b', fontWeight: 600,
          fontSize: 14, cursor: 'pointer', padding: 0, marginBottom: 12,
        }}
      >
        <ArrowLeft size={16} /> Retirada de equipamento
      </button>

      <h3 style={{ margin: '0 0 4px 0', fontSize: 20, fontWeight: 800, color: '#0f172a' }}>
        Nova solicitação
      </h3>
      <p style={{ margin: '0 0 20px 0', fontSize: 13.5, color: '#64748b' }}>
        {/* ⚠️ O resumo muda com a equipe — e antes da escolha ele DIZ que a
            escolha muda a tela, em vez de descrever uma das duas metades e
            surpreender a pessoa quando os campos trocarem. */}
        {cfg
          ? cfg.resumo
          : 'Escolha a equipe: na T.I você identifica o equipamento que vai sair da unidade; na Manutenção, as peças necessárias para resolver um problema.'}
      </p>

      {/* ---------------- A identificação ---------------- */}
      <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: compacto ? 14 : 20, marginBottom: 16 }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, marginBottom: 16 }}>
          {/* ⚠️⚠️ A EQUIPE É O PRIMEIRO CAMPO porque ela decide o resto da tela —
              as unidades disponíveis, os campos que aparecem e os rótulos. */}
          <div style={{ flex: compacto ? '1 1 100%' : '0 0 190px' }}>
            <label style={rotulo} htmlFor="ret-equipe">Equipe</label>
            <select
              id="ret-equipe" style={campo} value={equipe}
              onChange={(e) => setEquipe(e.target.value)}
            >
              <option value="">Selecione…</option>
              {Object.entries(EQUIPES).map(([v, e]) => (
                <option key={v} value={v}>{e.rotulo}</option>
              ))}
            </select>
          </div>

          <div style={{ flex: compacto ? '1 1 100%' : '0 0 190px' }}>
            <label style={rotulo} htmlFor="ret-loja">Unidade</label>
            <select
              id="ret-loja" style={campo} value={loja}
              onChange={(e) => setLoja(e.target.value)}
              // Uma unidade só = não há escolha; o campo fica visível (a pessoa
              // precisa VER de qual unidade se trata) e travado.
              disabled={!equipe || lojas.length <= 1}
            >
              <option value="">{equipe ? 'Selecione…' : 'Escolha a equipe primeiro'}</option>
              {lojas.map((l) => <option key={l} value={l}>{l}</option>)}
            </select>
          </div>

          <div style={{ flex: compacto ? '1 1 100%' : '0 0 auto', alignSelf: 'flex-end' }}>
            <label style={rotulo}>Foto</label>
            <FotoUpload
              url={fotoUrl}
              onUrl={setFotoUrl}
              label={fotoUrl ? 'Foto anexada' : 'Anexar foto'}
              api={API}
              campos={{ loja, chave: chaveArquivos }}
            />
            {/* Dizer que é opcional evita a pessoa fotografar qualquer coisa
                achando que o botão só libera com foto. */}
            <p style={{ margin: '6px 0 0 0', fontSize: 11.5, color: '#94a3b8' }}>opcional</p>
          </div>
        </div>

        {/* ---------------- O EQUIPAMENTO — só na T.I ---------------- */}
        {/* ⚠️⚠️ ESTE BLOCO SOME NA MANUTENÇÃO, e é o pedido literal da Julia. Lá
            o que se pede são PEÇAS, e um campo de equipamento obrigatório faria
            a pessoa ter de escolher um aparelho para pedir uma mangueira.
            ⚠️ Vem ANTES do motivo: o assunto da retirada é o equipamento, e o
            motivo se escreve olhando para ele. */}
        {cfg && !cfg.usaPecas && (
          <div style={{ marginBottom: 16, paddingBottom: 16, borderBottom: '1px solid #f1f5f9' }}>
            <label style={rotulo} htmlFor="ret-equip">Equipamento</label>
            {/* ⚠️ NÃO HÁ saída para texto livre aqui (ao contrário do que a
                Manutenção tinha): o parque de T.I cobre o que existe, e texto
                livre produziria "MONITOR DELL", "monitor dell 24" e "Mon. Dell"
                para o mesmo aparelho — ninguém responderia quantas vezes ele
                saiu da loja. O servidor recusa; isto aqui é a tela concordando. */}
            <select
              id="ret-equip" style={campo} value={ativoCodigo}
              onChange={(e) => setAtivoCodigo(e.target.value)}
              disabled={!loja}
            >
              <option value="">{loja ? 'Selecione…' : 'Escolha a unidade primeiro'}</option>
              {equipamentos.map((a) => (
                <option key={a.CODIGO} value={a.CODIGO}>
                  {[a.NOME, a.GRUPO].filter(Boolean).join(' · ')} ({a.CODIGO})
                </option>
              ))}
            </select>

            {/* ⚠️ CONFIRMA O QUE FOI ESCOLHIDO, e não é redundância: é este nome
                e este código que saem no comprovante que acompanha o
                equipamento, e a lista tem centenas de itens parecidos ("Monitor
                — balcão 1", "…balcão 2"). Escolher o vizinho é o erro provável. */}
            {escolhido && (
              <div style={{
                marginTop: 8, padding: '8px 11px', background: COR_CLARA,
                border: `1px solid ${COR_BORDA}`, borderRadius: 8, fontSize: 13,
              }}>
                Vai sair no comprovante como{' '}
                <strong>{[escolhido.NOME, escolhido.GRUPO].filter(Boolean).join(' — ')}</strong>,
                código <strong style={{ fontFamily: 'monospace' }}>{escolhido.CODIGO}</strong>.
              </div>
            )}

            {/* Lista vazia é anunciada em vez de mostrar um seletor mudo — um
                `<select>` só com "Selecione…" se lê como falha de carregamento. */}
            {loja && equipamentos.length === 0 && !erro && (
              <p style={{ margin: '6px 0 0 0', fontSize: 12, color: '#b45309' }}>
                Esta unidade não tem equipamento ativo no parque de T.I. Cadastre-o em
                <strong> Chamados › Equipamentos</strong> antes de registrar a retirada.
              </p>
            )}

            {/* ⚠️⚠️ O NÚMERO DE SÉRIE — OBRIGATÓRIO, e digitado.
                É ele que identifica o aparelho FÍSICO: o código do parque
                identifica o registro, e dois monitores do mesmo modelo têm
                registros diferentes que ninguém distingue olhando. Como o
                comprovante autoriza a saída do patrimônio e é conferido na
                volta, é a série que responde "voltou o mesmo aparelho?".
                ⚠️ Não vem preenchido do cadastro porque lá ele não existe:
                medido em 14/09, os 1.824 equipamentos ativos do parque de T.I
                estão TODOS sem série. */}
            <div style={{ marginTop: 14 }}>
              <label style={rotulo} htmlFor="ret-serie">Número de série</label>
              <input
                id="ret-serie" style={{ ...campo, fontFamily: 'monospace' }}
                value={numeroSerie}
                onChange={(e) => setNumeroSerie(e.target.value)}
                maxLength={80}
                disabled={!ativoCodigo}
                placeholder={ativoCodigo ? 'Como está na etiqueta do aparelho' : 'Escolha o equipamento primeiro'}
                autoComplete="off"
              />
              <p style={{ margin: '6px 0 0 0', fontSize: 12, color: '#64748b' }}>
                Copie exatamente como está na etiqueta. Ela sai no comprovante e é o que
                identifica este aparelho na volta.
              </p>
            </div>

            {/* ⚠️ FOTO DA SÉRIE — OBRIGATÓRIA. Serve para conferir a digitação:
                a série é copiada à mão da etiqueta e um dígito trocado só aparece
                na volta, quando já não dá para corrigir. Sobe no mesmo fluxo da
                foto geral (mesma pasta/chave); a URL vai no POST. */}
            <div style={{ marginTop: 14 }}>
              <label style={rotulo}>Foto do número de série</label>
              <FotoUpload
                url={fotoSerieUrl}
                onUrl={setFotoSerieUrl}
                label={fotoSerieUrl ? 'Foto da série anexada' : 'Anexar foto da série'}
                api={API}
                campos={{ loja, chave: chaveArquivos }}
              />
              <p style={{ margin: '6px 0 0 0', fontSize: 11.5, color: '#64748b' }}>
                Fotografe a etiqueta com o número de série legível — é a validação da digitação.
              </p>
            </div>
          </div>
        )}

        {/* ---------------- A PROSA — o rótulo muda com a equipe ---------------- */}
        {/* ⚠️ É a MESMA coluna (`MOTIVO`) nos dois casos, e é a mesma pergunta:
            *por que esta solicitação existe*. Duas colunas quase iguais é o
            defeito que a spec da Conservação mediu em campo, onde a pessoa
            escrevia a ordem de serviço no campo do problema. */}
        <label style={rotulo} htmlFor="ret-motivo">
          {cfg ? cfg.rotuloProsa : 'Motivo'}
        </label>
        <textarea
          id="ret-motivo" rows={4} maxLength={1000}
          style={{ ...campo, resize: 'vertical' }}
          placeholder={cfg ? cfg.dicaProsa : 'Escolha a equipe primeiro.'}
          value={motivo}
          onChange={(e) => setMotivo(e.target.value)}
          disabled={!cfg}
        />
        <p style={{ margin: '6px 0 0 0', fontSize: 11.5, color: '#94a3b8', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
          {motivo.length}/1000
        </p>

        {/* ---------------- O FORNECEDOR DO REPARO — só no T.I ---------------- */}
        {/* ⚠️ SÓ NO T.I aqui: o item é único (o equipamento), então o fornecedor
            do reparo é um só. Na MANUTENÇÃO ele é POR PEÇA (cada peça é uma
            solicitação de reparo) e aparece em cada linha de peça, abaixo.
            ⚠️ Não é o fornecedor da peça (de quem se compra).
            ⚠️ MAIÚSCULO na digitação — o servidor grava em caixa alta.
            ⚠️ O <datalist> sugere os já usados no MESMO equipamento. Sugestão,
            não trava — dá para digitar um novo. */}
        {cfg && !cfg.usaPecas && (
          <div style={{ marginTop: 16 }}>
            <label style={rotulo} htmlFor="ret-fornecedor-reparo">
              Fornecedor do reparo <span style={{ fontWeight: 400, color: '#94a3b8' }}>(opcional)</span>
            </label>
            <input
              id="ret-fornecedor-reparo" type="text" list="lista-fornecedor-reparo"
              style={{ ...campo, textTransform: 'uppercase' }}
              maxLength={150}
              value={fornecedorReparo}
              onChange={(e) => setFornecedorReparo(e.target.value.toUpperCase())}
              placeholder="Quem vai realizar o reparo (ex.: REFRIGERAÇÃO SILVA)"
              autoComplete="off"
            />
            <p style={{ margin: '6px 0 0 0', fontSize: 11.5, color: '#94a3b8' }}>
              {sugestoesFornecedor.length > 0
                ? 'Comece a digitar para ver os fornecedores já usados neste equipamento.'
                : 'Se já usou um fornecedor antes para este equipamento, ele aparece como sugestão.'}
            </p>
          </div>
        )}
      </div>

      {/* ---------------- AS PEÇAS — só na Manutenção ---------------- */}
      {/* ⚠️⚠️ SEM `overflow: hidden` NESTE CARD, e isso não é esquecimento. A lista
          suspensa do seletor de peça (ComboBusca) é `position: absolute`, e um
          ancestral com overflow escondido a CORTA — o campo abre e a pessoa não
          vê opção nenhuma, sem erro em lugar nenhum (foi o que aconteceu no
          primeiro teste desta tela). O canto arredondado do cabeçalho vem do
          `borderRadius` DELE, não do recorte do pai. */}
      {cfg && cfg.usaPecas && (
        <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, marginBottom: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 16px', background: '#f8fafc', borderBottom: '1px solid #e2e8f0', borderRadius: '12px 12px 0 0' }}>
            <strong style={{ fontSize: 12.5, fontWeight: 800, color: '#475569', textTransform: 'uppercase', letterSpacing: .3 }}>
              Peças necessárias
            </strong>
            <button
              type="button"
              onClick={() => setPecas((a) => [...a, PECA_VAZIA()])}
              style={{
                marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', gap: 6,
                padding: '7px 12px', background: '#fff', color: COR_ESCURA,
                border: `1px solid ${COR_BORDA}`, borderRadius: 8, fontWeight: 800, fontSize: 12.5, cursor: 'pointer',
              }}
            >
              <Plus size={14} /> Peça
            </button>
          </div>

          {pecas.map((p, i) => (
            <div key={p.uid} style={{ padding: compacto ? 14 : 16, borderTop: i ? '1px solid #f1f5f9' : 'none' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: p.origem ? 14 : 0 }}>
                <span style={{ fontSize: 12, fontWeight: 800, color: '#94a3b8', minWidth: 22, fontVariantNumeric: 'tabular-nums' }}>
                  {i + 1}
                </span>
                {botaoOrigem(p, 'Central', Package, 'Da Central de Pedidos')}
                {botaoOrigem(p, 'Fora', PackageSearch, 'Fora da Central')}
                {(pecas.length > 1 || p.origem) && (
                  <button
                    type="button" onClick={() => removerPeca(p.uid)} title="Remover esta peça"
                    style={{
                      marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', gap: 5,
                      padding: '7px 10px', background: '#fff', color: '#b91c1c',
                      border: '1px solid #fecaca', borderRadius: 8, fontWeight: 700, fontSize: 12.5, cursor: 'pointer',
                    }}
                  >
                    <Trash2 size={13} /> Remover
                  </button>
                )}
              </div>

              {/* Da Central: o seletor resolve código, descrição e fornecedor. */}
              {p.origem === 'Central' && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'flex-end' }}>
                  <div style={{ flex: '1 1 340px', minWidth: 0 }}>
                    <label style={rotulo}>Produto (catálogo da Central)</label>
                    <SelecaoPecaCentral
                      endpoint={ENDPOINT_SEM_PRECO}
                      onEscolher={(r) => mexerPeca(p.uid, {
                        idCatalogo: r ? r.ID : null,
                        codigo: r ? r.CODIGO : '',
                        descricao: r ? r.DESCRICAO : '',
                        fornecedor: r ? r.FORNECEDOR : '',
                      })}
                    />
                  </div>
                  <div style={{ flex: '0 0 110px' }}>
                    <label style={rotulo} htmlFor={`q-${p.uid}`}>Quantidade</label>
                    <input
                      id={`q-${p.uid}`} type="text" inputMode="decimal" style={campo}
                      value={p.quantidade}
                      onChange={(e) => mexerPeca(p.uid, { quantidade: e.target.value })}
                    />
                  </div>
                </div>
              )}

              {/* Peça escolhida: o que foi resolvido, para a pessoa CONFERIR. Não é
                  o que vai gravado — o servidor relê do catálogo. */}
              {p.origem === 'Central' && p.idCatalogo && (
                <p style={{ margin: '10px 0 0 0', fontSize: 12.5, color: '#475569' }}>
                  <strong>{p.descricao}</strong>
                  {p.codigo ? ` · código ${p.codigo}` : ''}
                  {p.fornecedor ? ` · ${p.fornecedor}` : ''}
                </p>
              )}

              {/* Fora da Central: a pessoa identifica o produto. Código e
                  fornecedor são opcionais — muita coisa se compra sem código, e
                  exigi-los faria alguém inventar um. */}
              {p.origem === 'Fora' && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
                  <div style={{ flex: '1 1 260px', minWidth: 0 }}>
                    <label style={rotulo} htmlFor={`d-${p.uid}`}>Produto</label>
                    <input
                      id={`d-${p.uid}`} type="text" style={campo} maxLength={500}
                      placeholder="Ex.: mangueira de gás 1/2"
                      value={p.descricao}
                      onChange={(e) => mexerPeca(p.uid, { descricao: e.target.value })}
                    />
                  </div>
                  <div style={{ flex: '0 0 110px' }}>
                    <label style={rotulo} htmlFor={`qf-${p.uid}`}>Quantidade</label>
                    <input
                      id={`qf-${p.uid}`} type="text" inputMode="decimal" style={campo}
                      value={p.quantidade}
                      onChange={(e) => mexerPeca(p.uid, { quantidade: e.target.value })}
                    />
                  </div>
                  <div style={{ flex: '0 1 150px' }}>
                    <label style={rotulo} htmlFor={`c-${p.uid}`}>Código <span style={{ fontWeight: 400, color: '#94a3b8' }}>(opcional)</span></label>
                    <input
                      id={`c-${p.uid}`} type="text" style={campo} maxLength={100}
                      value={p.codigo}
                      onChange={(e) => mexerPeca(p.uid, { codigo: e.target.value })}
                    />
                  </div>
                  <div style={{ flex: '1 1 190px' }}>
                    <label style={rotulo} htmlFor={`f-${p.uid}`}>Fornecedor <span style={{ fontWeight: 400, color: '#94a3b8' }}>(opcional)</span></label>
                    <input
                      id={`f-${p.uid}`} type="text" style={campo} maxLength={150}
                      value={p.fornecedor}
                      onChange={(e) => mexerPeca(p.uid, { fornecedor: e.target.value })}
                    />
                  </div>
                </div>
              )}

              {/* ⚠️ FORNECEDOR DO REPARO — POR PEÇA (cada peça é uma solicitação de
                  reparo). Aparece para as duas origens. Não é o fornecedor da peça
                  (de quem se compra), acima. MAIÚSCULO; sugere os já usados na loja. */}
              {p.origem && (
                <div style={{ marginTop: 12 }}>
                  <label style={rotulo} htmlFor={`fr-${p.uid}`}>
                    Fornecedor do reparo <span style={{ fontWeight: 400, color: '#94a3b8' }}>(opcional)</span>
                  </label>
                  <input
                    id={`fr-${p.uid}`} type="text" list="lista-fornecedor-reparo"
                    style={{ ...campo, textTransform: 'uppercase' }} maxLength={150}
                    value={p.fornecedorReparo}
                    onChange={(e) => mexerPeca(p.uid, { fornecedorReparo: e.target.value.toUpperCase() })}
                    placeholder="Quem vai realizar o reparo (ex.: REFRIGERAÇÃO SILVA)"
                    autoComplete="off"
                  />
                </div>
              )}

              {!p.origem && (
                <p style={{ margin: '10px 0 0 30px', fontSize: 12.5, color: '#94a3b8' }}>
                  Escolha acima de onde vem a peça para preencher os campos.
                </p>
              )}
            </div>
          ))}
        </div>
      )}

      {erro && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: 8, padding: '11px 14px',
          background: '#fee2e2', color: '#991b1b', borderRadius: 8, marginBottom: 14,
          fontSize: 13.5, fontWeight: 600,
        }}>
          <AlertTriangle size={16} /> {erro}
        </div>
      )}

      <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
        <button
          type="button" onClick={onVoltar} disabled={enviando}
          style={{
            padding: '10px 18px', background: '#fff', color: '#64748b',
            border: '1px solid #e2e8f0', borderRadius: 8, fontWeight: 700, cursor: 'pointer',
          }}
        >
          Cancelar
        </button>
        <button
          type="button" onClick={enviar} disabled={enviando}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 8,
            padding: '10px 20px', background: enviando ? '#67e8f9' : COR, color: '#fff',
            border: 'none', borderRadius: 8, fontWeight: 800,
            cursor: enviando ? 'wait' : 'pointer',
          }}
        >
          <Save size={16} /> {enviando ? 'Enviando…' : 'Enviar solicitação'}
        </button>
      </div>
    </div>
  );
};

export default RetiradaFormulario;
